(<template>
  <section :class="{'in-progress': progressActive}"
           class="page-data-section is-progress-bar certificate">
    <div class="certificate__header">
      <div class="certificate__header-content">
        <h3 class="page-data-section__title">{{ sectionTitle }}</h3>
        <p class="page-data-section__description">{{ sectionDescription }}</p>
      </div>
      <sk-upload-file :id="'certificate'"
                      :button-text="uploadButtonText"
                      :is-single="true"
                      :reset="fileInputReset"
                      class="certificate__upload-button"
                      @changefileinput="catchCertificate" />
    </div>
    <div v-if="userHasCertificate"
         class="certificate__uploaded">
      <div class="certificate__uploaded-icon"
           :class="{'certificate__uploaded-icon--approved': isStatusApproved}"></div>
      <div class="certificate__uploaded-text">
        <p class="certificate__uploaded-subtitle">{{ policeCertificateText }}</p>
        <p>{{ certificateReviewStatus }}</p>
      </div>
      <p class="certificate__uploaded-date">{{ updatedAt }}</p>
      <button class="certificate__remove-btn"
              :aria-label="deleteCertificateText"
              @click="removeCertificate"></button>
    </div>
    <div v-else>
      <div v-for="(step, index) in steps"
           :key="index"
           class="description-step">
        <div class="description-step__number">
          {{ index + 1 }}
        </div>
        <div class="description-step__content">
          <p class="description-step__text">{{ step.text }}</p>
          <button v-if="step.hasButton"
                  class="sk-btn sk-btn--white download-button"
                  @click="handleDownloadPurposeLetter">
            {{ step.text }}
          </button>
          <a v-if="step.linkLabel"
             :href="politietUrl"
             class="sk-btn sk-btn--white redirect-link"
             target="_blank">
            {{ step.linkLabel }}
          </a>
        </div>
      </div>
      <sk-note class="certificate__note"
               :text="noteText" />
      <sk-upload-file :id="'firstCertificate'"
                      :button-text="uploadButtonFirstText"
                      :is-single="true"
                      :reset="fileInputReset"
                      class="certificate__upload-button--first"
                      @changefileinput="catchCertificate" />
    </div>
  </section>
</template>)

<script>
  import {mapState} from 'vuex';

  export default {
    asyncData({store}) {
      const promisesArr = [];

      promisesArr.push(store.dispatch('CertificatesStore/getPoliceCertificate'));

      return promisesArr ? Promise.all(promisesArr) : '';
    },
    data() {
      return {
        progressActive: false,
        fileInputReset: false
      };
    },
    computed: {
      ...mapState('CertificatesStore', {
        certificate: (state) => state.certificate
      }),
      deleteCertificateText() { return this.$gettext('Remove certificate'); },
      policeCertificateText() { return this.$gettext('Police Certificate'); },
      certificateReviewStatus() {
        const status = this.certificate?.status || '';
        return status.charAt(0).toUpperCase() + status.slice(1);
      },
      userHasCertificate() { return !!this.certificate; },
      updatedAt() {
        const text = this.$gettext('Updated');
        return text + ' ' + this.$moment(this.certificate.updatedAt).fromNow();
      },
      isStatusApproved() { return this.certificate?.status === 'approved'; },
      uploadButtonText() { return this.$gettext('Upload'); },
      uploadButtonFirstText() { return this.$gettext('Upload Police Certificate'); },
      noteText() { return this.$gettext('<b>When you receive your certificate, upload it here</b> <br> After about 3-10 days, you will receive the certificate in your Digipost or by regular mail.'); },
      sectionTitle() { return this.$gettext('Police Certificate'); },
      politietUrl() { return 'https://www.politiet.no/tjenester/politiattest/sok-om-politiattest-for-privatpersoner/'; },
      sectionDescription() {
        return this.userHasCertificate
          ? this.$gettext('View and upload your Police Certificate here.')
          : this.$gettext('Follow the steps below to request a new Police Certificate');
      },
      steps() {
        return [
          {text: this.$gettext('Download Purpose Letter'), hasButton: true},
          {text: this.$gettext('Visit Police website'), linkLabel: 'politiet.no'},
          {text: this.$gettext('Log in with BankID')},
          {text: this.$gettext("Click 'Apply for another certificate'")},
          {text: this.$gettext("Under 'Category' select 'Tolk'")},
          {text: this.$gettext("Under 'Purpose' select 'Oppdrag som tolk for offentlige organer'")},
          {text: this.$gettext('Upload the Purpose Letter from the first step')},
          {text: this.$gettext('Submit')}
        ];
      }
    },
    methods: {
      catchCertificate(target) {
        this.progressActive = true;
        if (target.files && target.files.length) {
          this.$store.dispatch('uploadSingleFile', target.files[0])
            .then((uploadedFileData) => {
              const params = {
                police_certificate: {
                  document_file_signed_id: uploadedFileData.signedId
                }
              };
              this.$store.dispatch('CertificatesStore/sendPoliceCertificateFile', params).then(() => {
                this.$store.dispatch('CertificatesStore/getPoliceCertificate').then(() => {
                  this.progressActive = false;
                });
              });
            });
        }
      },
      removeCertificate() {
        this.progressActive = true;
        this.$store.dispatch('CertificatesStore/sendPoliceCertificateFile', {
          police_certificate: {document_file_signed_id: null}
        }).then(() => {
          this.$store.dispatch('CertificatesStore/getPoliceCertificate');
          this.progressActive = false;
        });
      },
      handleDownloadPurposeLetter() {
        this.progressActive = true;
        this.$store.dispatch('CertificatesStore/downloadPurposeLetter').then((data) => {
          const blob = new Blob([data], {type: 'application/pdf'});
          const downloadLink = document.createElement('a');
          downloadLink.href = URL.createObjectURL(blob);
          downloadLink.download = 'purpose-letter.pdf';
          downloadLink.click();
          this.progressActive = false;
          URL.revokeObjectURL(downloadLink.href);
        }).catch((error) => {
          this.progressActive = false;
          if (error?.errorCode == 'missing_required_information') {
            setTimeout(() => {
              this.$store.commit('ModalStore/setModal', {
                component: 'incomplete-profile-modal',
                data: {
                  errors: error?.errors
                }
              });
            });
          } else {
            setTimeout(() => {
              this.$store.commit('ModalStore/setModal', {
                component: 'error-modal',
                data: {
                  error
                }
              });
            });
          }
        });
      }
    }
  };
</script>

<style>
  .certificate__upload-button .sk-upload-file__label-btn::before {
    background-image: var(--plus-icon);
    background-size: 18px;
  }

  .certificate__upload-button--first input[type='file'] + .sk-upload-file__label-btn {
    background-color: var(--color-primary);
    color: #fff;
  }

  .certificate__upload-button--first .sk-upload-file__label-btn::before {
    background-image: url(~@assets/imgs/salita/icons/plus_icon_white.svg);
    background-size: 18px;
  }
</style>

<style scoped>
  .certificate__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .certificate__uploaded {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
  }

  .certificate__uploaded-text {
    flex-grow: 1;
  }

  .certificate__uploaded-icon {
    width: 48px;
    height: 48px;
    background-image: url(~@assets/imgs/salita/undefined_imgs/police_certificate_icon.svg);
    background-position: center;
    background-repeat: no-repeat;
  }

  .certificate__uploaded-icon--approved {
    background-image: url(~@assets/imgs/salita/undefined_imgs/police_certificate_icon_approved.svg);
  }

  .certificate__uploaded-subtitle {
    font-weight: bold;
  }

  .certificate__uploaded-date {
    margin-right: 2.2rem;
  }

  .certificate__remove-btn {
    width: 25px;
    height: 25px;
    background: var(--image-trash-icon) center/20px no-repeat;
  }

  .description-step {
    display: flex;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1rem;
  }

  .description-step__number {
    width: 32px;
    height: 32px;
    padding: 0.4rem;
    border-radius: 50%;
    background-color: #e4e6eb;
    color: #646d8c;
    font-weight: bold;
    font-size: 0.85rem;
    text-align: center;
  }

  .description-step__content {
    display: flex;
    gap: 1rem;
    align-items: center;
    width: 100%;
  }

  .download-button {
    width: fit-content;
    padding: 0 12px 0 33px;
    background: url(~@assets/imgs/salita/download_icon_black.svg) no-repeat left 12px center / 14px;
  }

  .redirect-link {
    width: fit-content;
    padding: 0 33px 0 12px;
    background: url(~@assets/imgs/salita/undefined_imgs/redirect_icon.svg) no-repeat right 12px center / 14px;
  }

  .certificate__upload-button {
    min-width: 100px;
  }

  .certificate__note {
    margin: 24px 0;
  }
</style>
